<template>
  <div class="seller-dashboard-home">
    <div class="page-header">
      <div class="header-content">
        <p class="header-eyebrow">Catalog Overview</p>
        <h1 class="page-title">商品管理</h1>
        <span class="product-count">当前共有 {{ activeProducts.length }} 件在售商品</span>
      </div>
      <router-link to="/seller/dashboard/product/new" class="btn btn-primary">
        <span class="icon">+</span>
        发布新商品
      </router-link>
    </div>

    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <span class="loading-text">正在加载商品数据...</span>
    </div>

    <div v-if="error && !loading" class="alert alert-danger">
      {{ error }}
    </div>

    <div v-if="!loading && activeProducts.length > 0" class="products-grid">
      <article
        v-for="product in activeProducts"
        :key="product.id"
        class="product-card"
        @mouseenter="hoveredProduct = product.id"
        @mouseleave="hoveredProduct = null"
      >
        <div class="product-image-wrapper">
          <img
            :src="product.imageUrl || 'https://picsum.photos/seed/dashboard-product-' + product.id + '/640/640'"
            :alt="product.name"
            class="product-image"
          />
          <div v-if="product.stock === 0" class="stock-badge sold-out">已售罄</div>
          <div v-else-if="product.stock < 10" class="stock-badge low-stock">库存紧张</div>
        </div>

        <div class="product-info">
          <div class="product-topline">
            <span class="category-tag">{{ product.categoryName || '未分类' }}</span>
            <span class="product-stock">库存 {{ product.stock }}</span>
          </div>

          <h3 class="product-name">{{ product.name }}</h3>

          <div class="product-price-row">
            <span class="product-price">¥{{ product.price.toFixed(2) }}</span>
          </div>

          <div class="product-stats">
            <span class="stat-item">浏览 {{ product.viewCount || 0 }}</span>
            <span class="stat-item">销量 {{ product.salesCount || 0 }}</span>
          </div>
        </div>

        <div class="product-actions">
          <router-link :to="'/seller/dashboard/product/new?id=' + product.id" class="action-btn ghost-btn">
            编辑
          </router-link>
          <button class="action-btn dark-btn" @click="confirmDelete(product)">
            删除
          </button>
        </div>
      </article>
    </div>

    <div v-if="!loading && activeProducts.length === 0" class="empty-state">
      <div class="empty-icon">□</div>
      <h3 class="empty-title">还没有上架商品</h3>
      <p class="empty-desc">从一件主打商品开始，把你的店铺视觉和货盘先搭起来。</p>
      <router-link to="/seller/dashboard/product/new" class="btn btn-primary">
        立即发布
      </router-link>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SellerDashboardHome',
  data() {
    return {
      activeProducts: [],
      loading: true,
      error: '',
      hoveredProduct: null
    }
  },
  created() {
    this.fetchActiveProducts()
  },
  methods: {
    async fetchActiveProducts() {
      try {
        this.loading = true
        const sellerId = localStorage.getItem('sellerId')
        if (!sellerId) {
          this.error = '请先登录'
          return
        }
        const response = await this.$axios.get(`/products/seller/${sellerId}/active`)
        this.activeProducts = Array.isArray(response.data) ? response.data : []
        this.error = ''
      } catch (err) {
        this.error = '获取商品信息失败'
        console.error('获取商品信息失败:', err)
      } finally {
        this.loading = false
      }
    },
    confirmDelete(product) {
      if (confirm(`确定要删除商品“${product.name}”吗？此操作不可恢复。`)) {
        this.deleteProduct(product.id)
      }
    },
    async deleteProduct(productId) {
      try {
        const sellerId = localStorage.getItem('sellerId')
        if (!sellerId) {
          this.error = '请先登录'
          return
        }
        await this.$axios.delete(`/products/${productId}/seller/${sellerId}`)
        this.fetchActiveProducts()
        this.error = ''
      } catch (err) {
        this.error = '删除商品失败，请重试'
        console.error(err)
      }
    }
  }
}
</script>

<style scoped>
.seller-dashboard-home {
  width: 100%;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1.4rem;
  border-bottom: 1px solid var(--color-border);
}

.header-eyebrow {
  margin: 0 0 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--color-text-faint);
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.page-title {
  margin: 0;
  font-size: 2rem;
  color: var(--color-text);
  letter-spacing: -0.03em;
}

.product-count {
  font-size: 0.96rem;
  color: var(--color-text-soft);
}

.btn-primary {
  background: linear-gradient(135deg, #111113, #2c2c2e);
}

.loading-container,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4.5rem 1.5rem;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.65);
  border: 1px solid var(--color-border);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(15, 23, 42, 0.08);
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  margin-top: 1rem;
  color: var(--color-text-soft);
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.25rem;
}

.product-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 26px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow: var(--shadow-soft);
  transition:
    transform var(--duration-base) var(--ease-standard),
    box-shadow var(--duration-base) var(--ease-standard);
}

.product-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-medium);
}

.product-image-wrapper {
  position: relative;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: linear-gradient(180deg, #fbfbfd, #f2f2f5);
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.stock-badge {
  position: absolute;
  top: 1rem;
  left: 1rem;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
}

.sold-out {
  background: rgba(192, 57, 43, 0.1);
  color: #9d3024;
}

.low-stock {
  background: rgba(183, 121, 31, 0.12);
  color: #8a5d15;
}

.product-info {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.85rem;
  padding: 1.2rem 1.2rem 1rem;
}

.product-topline,
.product-stats,
.product-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.category-tag,
.product-stock,
.stat-item {
  font-size: 0.84rem;
  color: var(--color-text-soft);
}

.category-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.28rem 0.6rem;
  background: rgba(15, 23, 42, 0.04);
  border-radius: 999px;
}

.product-name {
  margin: 0;
  font-size: 1.08rem;
  line-height: 1.4;
  color: var(--color-text);
}

.product-price-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.product-price {
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--color-text);
}

.product-actions {
  padding: 0 1.2rem 1.2rem;
}

.action-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  padding: 0.72rem 0.9rem;
  border-radius: 999px;
  text-decoration: none;
  font-weight: 600;
  transition:
    transform var(--duration-fast) var(--ease-standard),
    background-color var(--duration-fast) var(--ease-standard);
}

.ghost-btn {
  background: rgba(15, 23, 42, 0.05);
  color: var(--color-text);
}

.ghost-btn:hover {
  transform: translateY(-1px);
  background: rgba(15, 23, 42, 0.08);
}

.dark-btn {
  background: #1d1d1f;
  color: #fff;
  cursor: pointer;
}

.dark-btn:hover {
  transform: translateY(-1px);
  background: #2c2c2e;
}

.empty-icon {
  width: 72px;
  height: 72px;
  display: grid;
  place-items: center;
  margin-bottom: 1rem;
  border-radius: 20px;
  background: rgba(15, 23, 42, 0.04);
  color: var(--color-text-soft);
  font-size: 1.5rem;
}

.empty-title {
  margin: 0;
  font-size: 1.4rem;
  color: var(--color-text);
}

.empty-desc {
  max-width: 420px;
  margin: 0.75rem 0 1.5rem;
  text-align: center;
  line-height: 1.7;
  color: var(--color-text-soft);
}

@media (max-width: 768px) {
  .page-header {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
